import React from "react";
import { motion } from "framer-motion";
import {
  ClipboardCheck, Compass, DraftingCompass, Wrench, Activity, ShieldCheck, ArrowRight,
} from "lucide-react";

const PHASES = [
  {
    step: "01",
    title: "Assess",
    icon: ClipboardCheck,
    tone: { grad: "from-primary to-accent", chip: "bg-primary/10 text-primary" },
    tag: "Discovery & Risk",
    lines: [
      "Business context & data-flow mapping",
      "Cyber Risk Navigator™ Score",
      "Regulator-aligned gap analysis",
    ],
  },
  {
    step: "02",
    title: "Design",
    icon: DraftingCompass,
    tone: { grad: "from-accent to-primary", chip: "bg-accent/15 text-primary" },
    tag: "Architecture",
    lines: [
      "Zero-trust reference blueprint",
      "Control mapping to ISO / DPDP / SOC 2",
      "Vendor-neutral technology fit",
    ],
  },
  {
    step: "03",
    title: "Implement",
    icon: Wrench,
    tone: { grad: "from-secondary to-[#c2410c]", chip: "bg-secondary/10 text-secondary" },
    tag: "Delivery",
    lines: [
      "OEM-certified rollout & hardening",
      "Change control & knowledge transfer",
      "Evidence pack for audit",
    ],
  },
  {
    step: "04",
    title: "Manage",
    icon: Activity,
    tone: { grad: "from-primary to-[#0a3560]", chip: "bg-primary/10 text-primary" },
    tag: "Operate",
    lines: [
      "24×7 monitoring & incident response",
      "Patch, tuning & posture management",
      "Executive & regulator reporting",
    ],
  },
  {
    step: "05",
    title: "Assure",
    icon: ShieldCheck,
    tone: { grad: "from-accent to-secondary", chip: "bg-accent/15 text-primary" },
    tag: "Continuous Compliance",
    lines: [
      "Quarterly maturity reviews",
      "VAPT, IS audit & CERT-In drills",
      "Board-ready compliance evidence",
    ],
  },
];

const COMPLIANCE_TAGS = [
  "ISO 27001", "DPDP Act 2023", "SOC 2", "RBI", "SEBI CSCRF", "CERT-In", "PCI DSS", "NIST CSF 2.0",
];

export default function TechmateFramework() {
  return (
    <section id="framework" className="relative py-20 sm:py-28 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none -z-0">
        <div className="absolute -top-16 left-1/3 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 h-96 w-96 rounded-full bg-secondary/10 blur-3xl" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-secondary">Our Framework</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            How we <span className="text-gradient-brand">design, deliver &amp; manage</span> — aligned to your compliance.
          </h2>
          <p className="mt-4 text-muted-foreground text-base sm:text-lg leading-relaxed">
            A five-phase engagement model that connects strategy to execution. Every activity is
            mapped to the frameworks your regulators and customers care about — so security
            outcomes and audit evidence are produced together, not bolted on later.
          </p>
        </div>

        {/* Phase cards */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {PHASES.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`framework-phase-${p.step}`}
                className="relative card-lift rounded-3xl border border-border bg-card p-6 overflow-hidden"
              >
                <div className={`absolute -top-14 -right-14 h-40 w-40 rounded-full bg-gradient-to-br ${p.tone.grad} opacity-15 blur-2xl`} />
                <div className="relative">
                  <div className="flex items-center justify-between">
                    <div className={`h-14 w-14 rounded-2xl bg-gradient-to-br ${p.tone.grad} text-white grid place-items-center shadow-lg`}>
                      <Icon className="h-6 w-6" strokeWidth={1.6} />
                    </div>
                    <div className="font-display text-3xl font-black text-muted-foreground/25">{p.step}</div>
                  </div>
                  <div className="mt-4">
                    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${p.tone.chip}`}>
                      {p.tag}
                    </span>
                    <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">{p.title}</h3>
                  </div>
                  <ul className="mt-4 space-y-1.5">
                    {p.lines.map((l) => (
                      <li key={l} className="flex items-start gap-2 text-[13px] text-foreground/85">
                        <ArrowRight className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" />
                        <span className="leading-snug">{l}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Compliance rail */}
        <div className="mt-8 rounded-3xl border border-border bg-gradient-to-r from-primary/[0.06] via-accent/[0.05] to-secondary/[0.06] p-6 sm:p-7">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="shrink-0">
              <div className="text-xs font-bold uppercase tracking-widest text-secondary flex items-center gap-1.5">
                <Compass className="h-3.5 w-3.5" /> Aligned end-to-end with
              </div>
              <div className="mt-1 font-display text-lg font-semibold">
                Global &amp; Indian compliance frameworks
              </div>
            </div>
            <div className="flex-1 flex flex-wrap gap-2">
              {COMPLIANCE_TAGS.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-white/70 backdrop-blur px-3 py-1.5 text-xs font-bold text-primary"
                  data-testid={`framework-compliance-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <ShieldCheck className="h-3 w-3" /> {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
